'use client';

import { useRef } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { montserratMedium, montserratSemi } from "../fonts";
import HomeSectionTitle from './home-section-title';
import MagicBento from './magic-bento';
import { FeatureCard } from './tech-section';

interface TechSectionDesktopProps {
  featureCards: FeatureCard[];
}

export default function TechSectionDesktop({ featureCards }: TechSectionDesktopProps) {
  const t = useTranslations('home.tech');
  const techSectionRef = useRef<HTMLElement>(null);

  return (
    <section
      ref={techSectionRef}
      id="tech-section"
      className="section-container-full-height"
    >
      <HomeSectionTitle
        subtitle={t('subtitle')}
        title={t('title')}
        description={t('description')}
        centered
      />

      <div className="absolute top-[-12vh] left-[-50vw] lg:left-[-35vw] xl:left-[-25vw] z-10 pointer-events-none">
        <Image
          src="/assets/radio-3.svg"
          alt={t('altText')}
          width={805}
          height={863}
          loading="lazy"
        />
      </div>

      
      {/* Desktop: MagicBento */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full"
      >
        <MagicBento
          textAutoHide={false}
          enableStars={false}
          enableSpotlight={true}
          enableBorderGlow={false}       
          disableAnimations={false}
          enableTilt={true}
          enableMagnetism={false}
          clickEffect={false}
          spotlightRadius={150}
          particleCount={0}
          glowColor="11, 113, 255"
          cards={featureCards.map(card => ({
            title: card.title,
            description: card.description,
            icon: card.icon,
            hoverIcon: card.hoverIcon,
          }))}
          layoutVariant="uniform"
          cardClassName="card card--border-glow group flex flex-col gap-4  justify-start items-center rounded-3xl border border-[color:var(--field-border)] bg-transparent p-0 transition-all duration-300"
          renderCard={(card) => (
            <>
              <h3
                className={`${montserratSemi.className} pt-[32px] min-h-[96px] w-full text-start text-2xl px-6 text-[color:#f9f9f9] md:text-[color:var(--text-muted)] group-hover:text-[color:#f9f9f9]`}
              >
                <span
                  dangerouslySetInnerHTML={{ __html: String(card.title || "").replace(/\n/g, "<br/>") }}
                />
              </h3>
              <p
                className={`${montserratMedium.className} text-start text-sm text-[color:#d2d2d2] md:text-[color:var(--text-muted)] px-6 pt-2 pb-6 group-hover:text-[color:#d2d2d2]`}
              >
                {card.description}
              </p>
              <div className="text-4xl flex justify-center relative pb-4 h-full">
                <Image
                  src={(card.icon as string) || ""}
                  alt={(card.title as string) || ""}
                  width={640}
                  height={671}
                  className="opacity-50 group-hover:opacity-100 transition-opacity duration-300 object-contain w-[208px] h-auto"
                />
              </div>
            </>
          )}
        />
      </motion.div>
    </section>
  );
}

